import type { Theme } from "@/preferences-store";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { resolveApiBaseUrl, resolvePublicTenantHost } from "./api-base-url";

export interface BrandIdentity {
  logo: string | null;
  images: Record<string, string>;
  colors: Record<string, string>;
  texts: Record<string, string>;
  settings: { displayName?: string; defaultTheme?: Theme };
}
type Entry = { identity: BrandIdentity; savedAt: number };
type Asset = { data: string; savedAt: number };
interface BrandCache {
  active: Record<string, string | null>;
  entries: Record<string, Entry>;
  assets: Record<string, Asset>;
}
const EMPTY: BrandCache = { active: {}, entries: {}, assets: {} };
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const MAX_IMAGE_BYTES = 192 * 1024;
const verifiedTenants = new Map<string, string | null>();
const pendingImages = new Map<string, Promise<void>>();
const fresh = (savedAt: number) => savedAt <= Date.now() && Date.now() - savedAt < MAX_AGE;
const scopeOf = (host: string, tenant: string | null) => JSON.stringify([host, tenant]);

function bounded<T extends { savedAt: number }>(entries: Record<string, T>) {
  return Object.fromEntries(
    Object.entries(entries)
      .filter(([, entry]) => fresh(entry.savedAt))
      .sort((a, b) => b[1].savedAt - a[1].savedAt)
      .slice(0, 8),
  );
}

// Só identidade visual, sem sessão, permissões ou configurações administrativas.
// Storage bloqueado/cheio mantém o app funcionando com o cache em memória.
export const useBrandCache = create<BrandCache>()(
  persist(() => EMPTY, {
    name: "edu.brand-identity.v1",
    storage: createJSONStorage(() => ({
      getItem: (key) => {
        try {
          return localStorage.getItem(key);
        } catch {
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch {
          /* cache opcional */
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
        } catch {
          /* cache opcional */
        }
      },
    })),
    merge: (persisted, current) => {
      // Uma entrada inválida nunca impede o bootstrap do navegador.
      try {
        const state = persisted as BrandCache;
        if (!state || typeof state.active !== "object") return current;
        const entries = Object.fromEntries(
          Object.entries(state.entries ?? {}).filter(
            ([, entry]) =>
              entry &&
              fresh(entry.savedAt) &&
              entry.identity &&
              (entry.identity.logo === null || typeof entry.identity.logo === "string") &&
              entry.identity.images &&
              typeof entry.identity.images === "object" &&
              Object.values(entry.identity.images).every((value) => typeof value === "string") &&
              entry.identity.settings &&
              typeof entry.identity.settings === "object" &&
              (entry.identity.settings.displayName === undefined ||
                typeof entry.identity.settings.displayName === "string"),
          ),
        );
        const assets = Object.fromEntries(
          Object.entries(state.assets ?? {}).filter(
            ([, asset]) =>
              asset &&
              fresh(asset.savedAt) &&
              typeof asset.data === "string" &&
              /^data:image\/(?:png|jpeg|webp|gif|avif|svg\+xml);base64,/.test(asset.data) &&
              asset.data.length <= MAX_IMAGE_BYTES * 1.4,
          ),
        );
        const active = Object.fromEntries(
          Object.entries(state.active).filter(
            ([, tenant]) => tenant === null || typeof tenant === "string",
          ),
        );
        return { ...current, active, entries: bounded(entries), assets: bounded(assets) };
      } catch {
        return current;
      }
    },
  }),
);

export function activateBrandTenant(tenant: string | null) {
  const host = resolvePublicTenantHost();
  verifiedTenants.set(host, tenant);
  useBrandCache.setState((state) => ({ active: { ...state.active, [host]: tenant } }));
}
export function clearActiveBrandTenant() {
  const host = resolvePublicTenantHost();
  verifiedTenants.delete(host);
  useBrandCache.setState((state) => {
    const active = { ...state.active };
    delete active[host];
    return { active };
  });
}
export function authenticatedBrandScope(): string | undefined {
  const host = resolvePublicTenantHost();
  const tenant = verifiedTenants.get(host);
  return tenant ? scopeOf(host, tenant) : undefined;
}
export const publicBrandScope = (host: string) => scopeOf(host, null);

export function readStoredBrand(publicOnly = false): BrandIdentity | undefined {
  const { active, entries } = useBrandCache.getState();
  const host = resolvePublicTenantHost();
  const scope = scopeOf(host, publicOnly ? null : (active[host] ?? null));
  const entry = entries[scope];
  return entry && fresh(entry.savedAt) ? entry.identity : undefined;
}
export function useStoredBrand(publicOnly = false): BrandIdentity | undefined {
  const host = resolvePublicTenantHost();
  const tenant = useBrandCache((state) => (publicOnly ? null : (state.active[host] ?? null)));
  const entry = useBrandCache((state) => state.entries[scopeOf(host, tenant)]);
  return entry && fresh(entry.savedAt) ? entry.identity : undefined;
}
export function useBrandImage(source: string | null): string | null {
  const asset = useBrandCache((state) => (source ? state.assets[source] : undefined));
  return asset && fresh(asset.savedAt) ? asset.data : source;
}

export function cacheBrandImage(source: string): Promise<void> {
  const cached = useBrandCache.getState().assets[source];
  if (cached && fresh(cached.savedAt)) return Promise.resolve();
  if (pendingImages.has(source)) return pendingImages.get(source)!;
  const task = (async () => {
    try {
      const url = new URL(source, globalThis.location?.origin);
      if (!/^https?:$/.test(url.protocol)) return;
      // Mídia da API vem pela base da API do app: no subdomínio da marca é a
      // própria origem (proxy do front). O 307 servido por api-* não traz CORS
      // para o subdomínio, e o fetch falhava a cada navegação.
      const href = url.pathname.startsWith("/v1/media/")
        ? `${resolveApiBaseUrl()}${url.pathname}${url.search}`
        : url.href;
      const response = await fetch(href, { credentials: "omit", cache: "default" });
      if (!response.ok || Number(response.headers.get("content-length")) > MAX_IMAGE_BYTES) return;
      const blob = await response.blob();
      if (
        !/^image\/(png|jpeg|webp|gif|avif|svg\+xml)$/.test(blob.type) ||
        blob.size > MAX_IMAGE_BYTES
      )
        return;
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      useBrandCache.setState((state) => ({
        assets: bounded({ ...state.assets, [source]: { data, savedAt: Date.now() } }),
      }));
    } catch {
      /* CORS/rede: preserva a URL e o cache HTTP normal do navegador. */
    }
  })().finally(() => pendingImages.delete(source));
  pendingImages.set(source, task);
  return task;
}

export function rememberBrand(
  scope: string,
  data: {
    logo?: string | null;
    images?: Record<string, unknown>;
    colors?: Record<string, unknown>;
    texts?: Record<string, unknown>;
    settings?: { displayName?: string; defaultTheme?: Theme; darkModeDefault?: boolean };
  },
) {
  const images = Object.fromEntries(
    ["logo", "logoLight", "logoDark", "pwaIcon"].flatMap((key) => {
      const value = data.images?.[key];
      return typeof value === "string" && value ? [[key, value]] : [];
    }),
  );
  const identity: BrandIdentity = {
    logo: typeof data.logo === "string" ? data.logo : null,
    images,
    colors:
      typeof data.colors?.pwaBackground === "string"
        ? { pwaBackground: data.colors.pwaBackground }
        : {},
    texts: Object.fromEntries(
      ["pwaName", "pwaShortName"].flatMap((key) =>
        typeof data.texts?.[key] === "string" ? [[key, data.texts[key]]] : [],
      ),
    ),
    settings: {
      defaultTheme: ["light", "dark", "startup"].includes(data.settings?.defaultTheme ?? "")
        ? data.settings!.defaultTheme
        : typeof data.settings?.darkModeDefault === "boolean"
          ? data.settings.darkModeDefault
            ? "dark"
            : "light"
          : undefined,
      displayName:
        typeof data.settings?.displayName === "string" ? data.settings.displayName : undefined,
    },
  };
  useBrandCache.setState((state) => ({
    entries: bounded({ ...state.entries, [scope]: { identity, savedAt: Date.now() } }),
  }));
  for (const source of new Set([identity.logo, ...Object.values(images)])) {
    if (source) void cacheBrandImage(source);
  }
}
